import React from 'react'
import Head from 'components/Head'
import SiteLayout from 'components/SiteLayout'
import { usePageView } from 'hooks'
import { getStaticWrapper } from 'utils'

const TermsOfUse = () => {
  usePageView()
  return (
    <SiteLayout hideBanner>
      <Head title='Terms of Use | Barstool Sports' />
      <section className='container'>
        <article className='staticPage'>
          <header className='staticPage__header'>
            <h1>Terms of Use</h1>
          </header>
          <section className='staticPage__content'>
            <p>
              Barstool Sports, Inc. (&ldquo;Barstool&rdquo; or &ldquo;we&rdquo;, &ldquo;our&rdquo; or &ldquo;us&rdquo;)
              operates the <em>Barstool Sports</em> platform, which includes the Barstool Sports website, the Barstool
              Store, our subscription service(s) (if any), our affiliated websites, mobile apps, and OTT apps such as
              One Bite, Barstool Bets and Old Row, and all related social media pages (the foregoing, and all content,
              features, and materials thereon, shall hereinafter be collectively referred to as the
              &ldquo;Platform&rdquo;).
            </p>
            <p>
              These Terms of Use include our policy for acceptable use of the Platform. These Terms of Use also
              incorporate, by this reference, (1) our{' '}
              <a href='https://www.barstoolsports.com/privacy-policy'>Privacy Policy</a> which explains how we collect
              and use your and information and data, (2) our Exclusive Content License &amp;{' '}
              <a href='https://www.barstoolsports.com/content-policy'>Submission Policy</a> which governs your
              submission and our use of your content (&ldquo;Your Content&rdquo;), (3) our{' '}
              <a href='https://www.barstoolsports.com/subscription-terms'>Subscription Terms</a> which apply should you
              choose to purchase a subscription or one-time event through certain Barstool offerings; and (4) our{' '}
              <a href='http://www.barstoolsports.com/messaging-terms'>Messaging Terms and Conditions</a> which apply
              should you choose to receive recurring automated promotional and personalized marketing text messages from
              us. The Terms of Use, Privacy Policy and Exclusive Content License &amp; Submission Policy collectively
              shall be referred to as the &ldquo;Agreement&rdquo; and govern your rights, obligations and restrictions
              regarding your use of the Platform.&nbsp;
            </p>
            <p>By using the Platform, you agree to be bound by this Agreement.</p>
            <p>
              In order to participate in certain services on the Platform (e.g., if you make a purchase or enter a
              contest), you may be notified that you are required to agree to additional terms and conditions. Such
              additional terms are hereby incorporated into this Agreement by this reference but such additional terms
              shall control solely for the applicable service.
            </p>
            <p>
              We may modify this Agreement from time to time and such modification shall be effective upon posting on
              the Platform. You will be deemed to have agreed to any such modifications by your further use of the
              Platform after any such modification is posted. It is therefore important that you review this Agreement
              regularly to ensure you are updated as to any changes. If you do not agree with the modifications, please
              discontinue use of the Platform immediately.
            </p>

            <h2>Content</h2>

            <h3>Proprietary Rights</h3>
            <p>
              With the exception of content posted by users of the Platform (“User Content”), all materials contained on
              the Platform, including all content, and the software, graphics, text and look and feel of the Platform,
              and all trademarks (including BARSTOOL SPORTS®), copyrights, patents and other intellectual property
              rights related thereto (“Proprietary Materials”), are owned or controlled by Barstool., our subsidiaries
              or affiliated companies, contributors, our third party licensors, and/or our advertising partners. You may
              not modify, remove, delete, augment, add to, publish, transmit, participate in the transfer or sale of,
              create derivative works from, or in any way exploit any Proprietary Materials, or any other protectable
              aspects of the Platform, in whole or in part, unless specifically stated otherwise. Subject to your
              compliance with this Agreement and any other relevant policies related to the Platform, we grant you a
              non-exclusive, non-transferable, revocable limited license, subject to the limitations herein, to access
              and use the Platform and Proprietary Materials for your own non-commercial entertainment purposes
              consistent with the intended purpose of the Platform. You agree not to use the Platform for any other
              purpose.
            </p>

            <h3>Validity of Assertions</h3>
            <p>
              Our Platform is intended to provide you with humorous content and contains published rumors, speculations,
              assumptions and opinions in addition to factual information. We often are commenting on articles published
              by third parties. Therefore, any statements about facts not personally known to our authors should be
              interpreted as merely statements of alleged facts.
            </p>

            <h3>Distribution/Uploading Of Content</h3>
            <p>
              All terms relating to the posting, use and distribution of Your Content can be found in our{' '}
              <a href='/content-policy'>Exclusive Content License & Submission Policy</a>.
            </p>

            <h3>Third-Party Content &amp; User Content</h3>
            <p>
              You acknowledge that Barstool is an online service provider that may post content provided by users. Any
              opinions, advice, statements, judgments, services, offers, or other information that constitutes part of
              the content expressed or made available by third parties, including content posted by our contributors or
              User Content, are those of the respective authors and not of Barstool or its affiliates or any of their
              officers, directors, employees, or agents. While we retain the right to filter or reject User Content and
              other content posted by third parties, we review User Content and other third party content in a limited
              gate-keeper fashion and have no obligation to investigate whether any content or other User Content
              violate any term of this Agreement and will not do so in most cases. We neither endorse nor are
              responsible for the User Content or any other content posted to the Platform by anyone other than
              Barstool.
            </p>
            <p>
              Just as you retain complete ownership of Your Content (subject to the license to us granted above), all
              other users of the Platform retain ownership of their User Content. You may not modify, remove, delete,
              augment, add to, publish, transmit, participate in the transfer or sale of, create derivative works from,
              or in any way exploit any User Content in whole or in part.
            </p>

            <h3>Subscription and Other Purchased Content</h3>
            <p>
              We may offer certain content on a subscription or one-time-purchase basis that will require you to pay
              fee(s) in order to access such content. All recurring subscriptions and other content purchases will be
              governed by our <a href='/subscription-terms'>Subscription Terms</a>.
            </p>

            <h3>Content Related to Gambling/Betting</h3>
            <p>
              Certain sites within our Platform, including Barstool Bets, may include content related to gambling/sports
              betting. All this content is being provided for entertainment purposes only. It should not be seen as
              advice or the endorsement of any form of gambling. Our Platform does not give you the opportunity to
              gamble or wager anything of value. If you choose to gamble outside of our Platform, you do so at your own
              risk.&nbsp;
            </p>
            <p>
              If you have concerns about your gambling - or if you&rsquo;re concerned about a friend or family member
              &ndash;&nbsp;<a href='http://www.ncpgambling.org/'>National Council on Problem Gambling</a>&nbsp;(NCPG)
              offers support and counseling referrals that may help you through personal, gaming-related issues.
              The&nbsp;<a href='http://www.ncpgambling.org/'>NCPG</a>&nbsp;operates a 24-hour confidential toll-free
              helpline that you can reach by phone or text at&nbsp;<a href='about:blank'>1-800-522-4700</a>&nbsp;or chat
              at&nbsp;<a href='http://www.ncpgambling.org/chat'>ncpgambling.org/chat</a>. For additional information or
              resources relating to problems with gambling, please visit the websites for{' '}
              <a href='http://www.ncpgambling.org/'>National Council for Problem Gambling</a> and/or{' '}
              <a href='https://www.ncrg.org/'>National Center for Responsible Gaming</a>.
            </p>

            <h2>Conduct</h2>
            <p>
              You, as a user, agree to use the Platform only for lawful purposes. Without limiting anything else above,
              specific prohibited activities include, but are not limited to:
            </p>
            <ol>
              <li>
                depicting, encouraging or partaking in criminal or tortious activity, including fraud, trafficking in
                obscene or illegal material, drug dealing or using, underage drinking, gambling, violence, harassment,
                stalking, spamming, spimming, sending of viruses or other harmful files,&nbsp;
              </li>
              <li>
                depicting, encouraging or partaking in dangerous activities or conduct that is abusive, threatening,
                obscene, defamatory or libelous;&nbsp;
              </li>
              <li>
                posting, uploading or sharing content that violates any of the prohibitions herein or that constitutes
                copyright infringement, patent infringement, or theft of trade secrets;
              </li>
              <li>
                attempting to circumvent, disable or otherwise interfere with security-related features of the Platform
                or features that prevent or restrict use or copying of any content or enforce limitations on use of the
                Platform or the content therein;
              </li>
              <li>
                using any software that intercepts, mines, or otherwise collects information about other users or copies
                and stores any Proprietary Materials (as defined below);
              </li>
              <li>
                interfering with, disrupting, or creating an undue burden on the Platform or the networks or services
                connected to the Platform;
              </li>
              <li>attempting to impersonate another user or person;</li>
              <li>soliciting personal information from anyone under 18;</li>
              <li>
                collecting, harvesting, soliciting or posting passwords or personally identifiable information from
                other users;
              </li>
              <li>
                using the account, username, or password of another account holder at any time or disclosing your
                password to any third party or permitting any third party to access your Account;&nbsp;
              </li>
              <li>
                using any information obtained from the Platform in order to harass, abuse, or harm another person,
                whether a user or a contributor;&nbsp;
              </li>
              <li>using the Platform a commercial manner.</li>
            </ol>
            <p>
              You represent and warrant that neither your actions on the Platform nor Your Content will violate any of
              the prohibited conduct described above. Any conduct by you in violation of the foregoing prohibitions may
              result in the suspension or termination of your Account and your access to the Platform.
            </p>

            <section id='copyright'>
              <h2>Copyright</h2>
              <p>
                If you believe that any content, including User Content or other materials, posted on the Platform
                constitute an infringement of your copyrights, we will respond promptly to any properly submitted notice
                containing the information detailed below. Pursuant to Title 17, United States Code, Section 512(c)(2),
                written notifications of claimed copyright infringement should be sent to our Designated Agent at the
                following contact information:{' '}
                <a href='mailto:copyright@barstoolsports.com'>copyright@barstoolsports.com</a>.
              </p>
              <p>To be effective, the notification must be a written communication that includes the following:</p>
              <ol>
                <li>
                  A physical or electronic signature of person authorized to act on behalf of the owner of an exclusive
                  right that is allegedly infringed;
                </li>
                <li>
                  Identification of the copyrighted work claimed to have been infringed, or if multiple copyrighted
                  works at a single online site are covered by a single notification, a representative list of such
                  works at that site;
                </li>
                <li>
                  Identification of the material that is claimed to be infringing or to be the subject of infringing
                  activity and that is to be removed or access to which is to be disabled, and information reasonably
                  sufficient to permit us to locate the material;
                </li>
                <li>
                  Information reasonably sufficient to permit us to contact the complaining party, such as an address,
                  telephone number, and if available, an electronic mail address at which the complaining party may be
                  contacted;
                </li>
                <li>
                  A statement that the complaining party has a good faith belief that use of the material in the manner
                  complained of is not authorized by the copyright owner, its agent, or the law; and
                </li>
                <li>
                  A statement that the information in the notification is accurate, and under penalty of perjury, that
                  the complaining party is authorized to act on behalf of the owner of an exclusive right that is
                  allegedly infringed.
                </li>
              </ol>
            </section>
            <h2>Your Account / Password Security / Suspension, Termination</h2>
            <p>
              In order to access certain features of the Platform (e.g., comments section), you will be required to
              register for an account with us (an "Account"). When creating or updating an Account, you are required to
              provide us with certain personal information, such as your e-mail address or, in the case of purchases,
              your name, billing/shipping address and credit card information. This information will be held and used in
              accordance with our <a href='/privacy-policy'>Privacy Policy</a>.
            </p>
            <p>
              You may never use another's Account without permission or permit another to use your Account. You may not
              create more than one Account. You are prohibited from registering a new Account if you have previously had
              an Account terminated.
            </p>
            <p>
              You are responsible for (i) keeping confidential any password that you created to use any aspect of the
              Platform requiring registration and (ii) restricting access to your computer or mobile device. You agree
              to accept full responsibility for all activities that occur within your Account. You must notify us
              immediately of any breach of security or unauthorized use of any part of your Account.&nbsp;
            </p>
            <p>
              Although we will not be liable for your losses caused by any unauthorized use of your Account, whether
              with or without your knowledge, you may be liable for our losses or the losses of our contributors,
              third-party licensors, content providers, merchants, advertisers, sponsors, and service providers
              (collectively, &ldquo;Barstool Providers") due to such unauthorized use.{' '}
            </p>
            <p>
              Without limiting anything in this Agreement, we reserve the right, in our sole discretion, to restrict,
              suspend, or terminate your Account and/or your access to all or any part of the Platform at any time, for
              any or no reason, with or without prior notice, and without liability, including in the event we stop
              offering any aspect of the Platform. We expressly reserve the right to restrict, suspend and/or terminate
              your access to any part of the Platform if we determine, in our sole discretion, that you have violated
              any of the terms of this Agreement.
            </p>

            <h2>Links</h2>
            <p>
              The Platform may contain links or otherwise direct you to websites operated by third parties. We do not
              monitor or control the linked sites and make no representations regarding, and are not liable or
              responsible for the accuracy, completeness, timeliness, reliability or availability of, any of the content
              uploaded, displayed, or distributed, or products, or services available at these sites. If you choose to
              access any third party site, you do so at your own risk. The presence of a link to a third party site does
              not constitute or imply our endorsement, sponsorship, or recommendation of the third party or of the
              content, products, or services contained on, or available through, the third party site.
            </p>
            <p>We reserve the right to disable links from or to third party sites.</p>

            <h2>Barstool Stores</h2>
            <p>
              If you order products from one of the Stores made available through our Platform, including the official
              Barstool Store (located at https://store.barstoolsports.com), the following terms apply:
            </p>

            <h3>Products/Orders</h3>
            <p>
              Certain products or services may be available exclusively online. These products or services may have
              limited quantities and are subject to return or exchange only according to our Return Policy.
            </p>
            <p>
              We have made every effort to display as accurately as possible the colors and images of our products that
              appear at the store. We cannot guarantee that your computer monitor's display of any color will be
              accurate.
            </p>
            <p>
              We reserve the right to limit the quantities of any products or services that we offer. All descriptions
              of products are subject to change at any time without notice, at the sole discretion of us. We reserve the
              right to discontinue any product at any time. Any offer for any product made on this site is void where
              prohibited.
            </p>
            <p>
              We reserve the right to refuse any order you place with us. In the event that we cancel an order, we may
              attempt to notify you by contacting the e-mail and/or billing address/phone number provided at the time
              the order was made. We reserve the right to limit or prohibit orders that, in our sole judgment, appear to
              be placed by dealers, resellers or distributors.
            </p>

            <h3>Pricing/Taxes</h3>
            <p>
              All pricing is subject to change without prior notice. We reserve the right to adjust the price of any
              product sold through our online stores at any time in our sole discretion. In the event that a product is
              listed on the Website at an incorrect price due to a typographical error or error in pricing information,
              we will have the right to refuse or cancel any orders placed for the product listed at the incorrect
              price. We will have the right to refuse or cancel any such orders whether or not the order has been
              confirmed and your credit card charged. If your credit card has already been charged for the purchase and
              your order is canceled, we will make reasonable attempts to notify you and will issue a credit to your
              credit card account in the amount of the incorrect price.&nbsp;
            </p>
            <p>
              To the extent that we are required by law to charge and collect taxes on products that we sell, such taxes
              are charged based on the tax laws applicable to the location to which the order is being shipped or
              delivered. At checkout, all appropriate taxes will be added to the order total. The tax amount displayed
              during checkout is an estimate of the tax applicable to your order. This amount may vary slightly from the
              actual amount of tax payable in connection with your order due to different tax rates which apply as a
              result of the origin and destination of the item(s) being purchased, as well as other factors.
            </p>

            <h2>Third-party Merchants / Providers</h2>
            <p>
              The Platform may enable you to order and receive products, information and services from businesses that
              are not owned or operated by us. The purchase, payment, warranty, guarantee, delivery, maintenance, and
              all other matters concerning the merchandise, services or information, opinion or advice ordered or
              received from such businesses are solely between you and such businesses. We do not endorse, warrant, or
              guarantee such products, information, or services, and are not liable for the accuracy, completeness, or
              usefulness of such information or the quality of availability of such products or services. We will not be
              a party to or in any way be responsible for monitoring any transaction between you and third-party
              providers of such products, services, or information, or for ensuring the confidentiality of your credit
              card information. Any separate charges or obligations you incur in your dealings with these third parties
              are your responsibility and are not part of the fee, if any, charged for the Platform.
            </p>
            <p>
              In addition, the Platform may prompt you to establish an account with a third party service provider not
              owned or operated by us. Your agreement and understanding with any such third party service provider is
              solely between you and such service provider. We will not be a party to or in any way be responsible for
              your agreement with such third-party service provider. Any disputes you may encounter with such third
              party service provider shall be settled solely between you and the service provider.
            </p>

            <h2>Disclaimers and Limitation of Liability</h2>
            <p>
              THE PLATFORM AND ALL PRODUCTS AND SERVICES OFFERED HEREON ARE PROVIDED BY US AND OUR WEBHOST ON AN "AS IS"
              AND "AS AVAILABLE" BASIS. TO THE FULLEST EXTENT PERMISSIBLE BY APPLICABLE LAW, WE DISCLAIM ALL IMPLIED
              WARRANTIES, INCLUDING BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A
              PARTICULAR PURPOSE.
            </p>
            <p>
              WITHOUT LIMITING THE FOREGOING, NEITHER WE NOR ANY BARSTOOL PROVIDER MAKES ANY REPRESENTATION OR WARRANTY
              OF ANY KIND, EXPRESS OR IMPLIED: (I) AS TO THE OPERATION OF THE PLATFORM, OR THE INFORMATION, CONTENT,
              MATERIALS OR PRODUCTS INCLUDED THEREON; (II) THAT USE OF THE PLATFORM WILL BE 100% SECURE, UNINTERRUPTED
              OR ERROR-FREE; (III) AS TO THE ACCURACY, RELIABILITY, OR CURRENCY OF ANY INFORMATION, CONTENT, OR SERVICE,
              PROVIDED THROUGH THE PLATFORM; OR (IV) THAT THE SERVERS, OR EMAILS SENT FROM OR ON BEHALF OF BARSTOOL, ARE
              FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.&nbsp;
            </p>
            <p>
              BARSTOOL EXPRESSLY DISCLAIMS ANY AND ALL LIABILITY FOR THE ACTIONS OF ITS USERS OR THE CONTENTS OF ANY
              USER CONTENT.
            </p>
            <p>
              NEITHER WE NOR ANY BARSTOOL PROVIDER WILL BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL OR CONSEQUENTIAL
              DAMAGES ARISING FROM THE USE OF THE PLATFORM OR THE PURCHASE OF ANY PRODUCT THEREFROM, EVEN IF WE OR SUCH
              BARSTOOL PROVIDER HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. UNDER NO CIRCUMSTANCES WILL
              BARSTOOL BE LIABLE TO YOU FOR MORE THAN THE AMOUNT YOU HAVE PAID BARSTOOL IN THE ONE HUNDRED AND EIGHTY
              (180) DAYS IMMEDIATELY PRECEDING THE DATE ON WHICH YOU FIRST ASSERT ANY SUCH CLAIM AND IF YOU HAVE NOT
              PAID ANY AMOUNTS DURING THAT TIME PERIOD, YOU ACKNOWLEDGE THAT YOUR SOLE REMEDY SHALL BE TO CEASE USING
              THE PLATFORM AND TERMINATE YOUR ACCOUNT.
            </p>
            <p>
              CERTAIN STATE LAWS DO NOT ALLOW LIMITATIONS ON IMPLIED WARRANTIES OR THE EXCLUSION OR LIMITATION OF
              CERTAIN DAMAGES. IF THESE LAWS APPLY TO A USER, SOME OR ALL OF THE ABOVE DISCLAIMERS, EXCLUSIONS, OR
              LIMITATIONS MAY NOT APPLY TO SUCH USER, AND SUCH USER MAY HAVE ADDITIONAL RIGHTS.
            </p>

            <h2>Legality</h2>
            <p>
              You are subject to all laws of the state(s) and countries in which you reside and from which you access
              the Platform and are solely responsible for obeying those laws. You agree we cannot be held liable if laws
              applicable to you restrict or prohibit your participation. We make no representations or warranties,
              implicit or explicit, as to your legal right to participate in any Platform, contests, sweepstakes or
              tournaments offered through the Platform nor shall any person affiliated, or claiming affiliation with the
              Platform have authority to make any such representations or warranties.
            </p>

            <h2>Applicable Law; Jurisdiction</h2>
            <p>
              The Platform are created and controlled by us in the State of New York. As such, the laws of the State of
              New York will govern this Agreement, without giving effect to any provisions of New York law that direct
              the choice of another state&rsquo;s laws.
            </p>
            <p>
              Subject to the Binding Arbitration section below, you hereby irrevocably and unconditionally consent to
              submit to the exclusive jurisdiction of the courts of the State of New York and of the United States of
              America located in the State of New York for any litigation arising out of or relating to use of or
              purchase made through the Platform (and agree not to commence any litigation relating thereto except in
              such courts), waive any objection to the laying of venue of any such litigation in the New York Courts and
              agree not to plead or claim in any New York Court that such litigation brought therein has been brought in
              an inconvenient forum.
            </p>

            <h2>Binding Arbitration</h2>
            <p>
              Any controversy or claim arising out of or relating to this Agreement or any related agreement, their
              enforcement or interpretation, or because of an alleged breach, default or misrepresentation in connection
              with any of their provisions, shall be determined by binding arbitration. The arbitration proceedings
              shall be held and conducted by a single arbitrator in accordance with the Comprehensive Arbitration Rules
              and Procedures of JAMS (the &ldquo;JAMS Rules&rdquo;), as modified by this Agreement. Such arbitration
              shall occur in New York, New York, and be initiated by any party in accordance with the JAMS Rules. The
              demand for arbitration shall be made by any party hereto within a reasonable time after the claim, dispute
              or other matter in question has arisen, and in any event shall not be made after the date when institution
              of legal proceeding, based on such claim, dispute or other matter in question, would be barred by the
              applicable statute of limitations. Discovery issues shall be decided by the arbitrator. Post-hearing
              briefs shall be permitted. The arbitrator shall render a decision within twenty (20) days after the
              conclusion of the hearing(s). In reaching a decision, the arbitrator shall have no authority to change,
              extend, modify or suspend any of the terms of this Agreement, or to grant an award or remedy any greater
              than that which would be available from a court under the statutory or common law theory asserted. The
              arbitrator shall issue a written opinion that includes the factual and legal basis for any decision and
              award. The arbitrator shall apply the substantive law (and the law of remedies, if applicable) of New York
              or federal law, or any of them, as applicable to the claim(s) asserted. Judgment on the award may be
              entered in any court of competent jurisdiction. The parties may seek, from a court of competent
              jurisdiction, provisional remedies or injunctive relief in support of their respective rights and remedies
              hereunder without waiving any right to arbitration. However, the merits of any action that involves such
              provisional remedies or injunctive relief, including, without limitation, the terms of any permanent
              injunction, shall be determined by arbitration under this paragraph. Judgment upon the award rendered by
              the arbitrator may be entered in any court having jurisdiction thereof. The arbitrator shall allocate all
              costs and expenses of the arbitration (including legal and accounting fees and expenses of the respective
              parties) to the parties in the proportions that reflect their relative success on the merits (including
              the successful assertion of any defenses).&nbsp;
            </p>
            <p>
              You agree to pursue any arbitration in an individual capacity and not as class representative or class
              member in any purported class action proceeding.
            </p>
            <p>
              BY AGREEING TO ARBITRATE DISPUTES, THE PARTIES HEREBY ACKNOWLEDGE AND AGREE THAT THEY ARE IRREVOCABLY
              WAIVING ANY AND ALL RIGHT TO TRIAL BY JURY IN ANY LEGAL PROCEEDING ARISING OUT OF OR RELATING TO THIS
              AGREEMENT OR THE ACTIVITIES CONTEMPLATED HEREBY.
            </p>

            <h2>Indemnity</h2>
            <p>
              You agree to indemnify and hold Barstool, the Barstool Providers, our subsidiaries, and affiliates, and
              our respective officers, agents, partners and employees, harmless from any loss, liability, claim, or
              demand, including reasonable attorneys' fees, made by any third party due to or arising out of (1) your
              breach of this Agreement and/or any of your representations and warranties set forth herein (including
              those set forth in the Content Submission Policy) or (2) our distribution, exploitation or other use of
              Your Content.
            </p>

            <h2>Severability</h2>
            <p>
              The provisions of this Agreement are intended to be severable. If for any reason any provision of this
              Agreement shall be held invalid or unenforceable in whole or in part in any jurisdiction, such provision
              shall, as to such jurisdiction, be ineffective to the extent of such invalidity or unenforceability
              without in any manner affecting the validity or enforceability thereof in any other jurisdiction or the
              remaining provisions hereof in any jurisdiction.
            </p>

            <h2>Electronic Communications</h2>
            <p>
              When you use the Platform or send emails to us, you are communicating with us electronically. You consent
              to receive communications from us electronically. We will communicate with you by email or by posting
              notices through the Platform. You agree that all agreements, notices, disclosures and other communications
              that we provide you electronically satisfy any legal requirement that such communications be in writing.
            </p>

            <h2>Other</h2>
            <p>
              This Agreement is deemed accepted upon any use of any of the Platform. Our failure to exercise or enforce
              any right or provision of this Agreement shall not operate as a waiver of such right or provision. The
              section titles in this Agreement are for convenience only and have no legal or contractual effect.
            </p>

            <h2>Support</h2>
            <p>
              Subject to the other provisions of this Agreement, we will attempt to help you with any queries or
              problems that you may have with the Platform or any of your purchases through the Platform. To reach our
              customer support team, please e-mail us at support@barstoolsports.com. It will expedite your request for
              assistance by providing our representatives with all the information they need to solve your problem as
              quickly as possible.
            </p>

            <h2>Third Party Marketplaces</h2>
            <p>
              This Agreement is between you and Barstool only, not with Apple, Facebook, Instagram, Google or any other
              third party through which you may have accessed the Platform (“Marketplaces”). The Marketplaces are not
              responsible for the Platform. The Marketplaces have no obligation whatsoever to furnish any maintenance
              and support services with respect to the Platform. To the maximum extent permitted by applicable law, the
              Marketplaces have no other warranty obligation whatsoever with respect to the Platform. The Marketplaces
              are not responsible for addressing any claims by you or any third party relating to the Platform or your
              possession and/or use of the Platform, including, but not limited to: (i) product liability claims; (ii)
              any claim that the Platform fails to conform to any applicable legal or regulatory requirement; (iii)
              claims arising under consumer protection or similar legislation or (iv) claims that the Platform or any
              content thereon infringes upon a third party’s intellectual property rights.
            </p>

            <h2>Modification</h2>
            <p>
              We reserve the right to make changes to the Platform, posted policies and this Agreement at any time
              without notice other than the reposting of the modified Agreement so check back frequently.{' '}
            </p>
            <p>
              Please contact us at <a href='mailto:support@barstoolsports.com'>support@barstoolsports.com</a> with any
              questions regarding this Agreement.
            </p>
          </section>
        </article>
      </section>
    </SiteLayout>
  )
}

export const getStaticProps = getStaticWrapper()

export default TermsOfUse
